import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  MoreHorizontal,
  UserPlus,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  role: "host" | "attendee" | "moderator";
}

interface ParticipantsProps {
  participants: User[];
  isHost: boolean;
}

const Participants: React.FC<ParticipantsProps> = ({
  participants,
  isHost,
}) => {
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [searchText, setSearchText] = useState("");

  const filteredParticipants = participants.filter(
    (p) =>
      p.name.toLowerCase().includes(searchText.toLowerCase()) ||
      p.email.toLowerCase().includes(searchText.toLowerCase())
  );

  const handleInvite = () => {
    // In a real implementation, this would send an invitation
    setInviteEmail("");
    setShowInviteDialog(false);
  };

  const handleMuteParticipant = (userId: string) => {
    // In a real implementation, this would mute the participant
    console.log("Mute participant:", userId);
  };

  const handleRemoveParticipant = (userId: string) => {
    // In a real implementation, this would remove the participant
    console.log("Remove participant:", userId);
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-medium">
          Participants ({participants.length})
        </h3>
        {isHost && (
          <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
            <DialogTrigger asChild>
              <Button size="sm" variant="outline" className="flex items-center">
                <UserPlus className="h-4 w-4 mr-1" /> Invite
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Invite Participants</DialogTitle>
                <DialogDescription>
                  Send invitation links to attendees via email.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="space-y-2">
                  <label htmlFor="email" className="text-sm font-medium">
                    Email address
                  </label>
                  <Input
                    id="email"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="colleague@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <label htmlFor="role" className="text-sm font-medium">
                    Role
                  </label>
                  <select className="w-full rounded-md border border-gray-300 p-2 text-sm">
                    <option value="attendee">Attendee</option>
                    <option value="moderator">Moderator</option>
                  </select>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setShowInviteDialog(false)}
                >
                  Cancel
                </Button>
                <Button onClick={handleInvite}>Send Invitation</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="mb-4">
        <Input
          placeholder="Search participants..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          className="w-full"
        />
      </div>

      <div className="flex-grow overflow-y-auto">
        <ul className="space-y-2">
          {filteredParticipants.map((participant) => (
            <li
              key={participant.id}
              className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-md"
            >
              <div className="flex items-center">
                <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                  {participant.avatar ? (
                    <img
                      src={participant.avatar}
                      alt={participant.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-300 text-gray-600">
                      {participant.name.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="ml-2">
                  <div className="text-sm font-medium flex items-center">
                    {participant.name}
                    {participant.role === "host" && (
                      <span className="ml-1 text-xs bg-gray-200 px-1 rounded">
                        Host
                      </span>
                    )}
                    {participant.role === "moderator" && (
                      <span className="ml-1 text-xs bg-gray-200 px-1 rounded">
                        Mod
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500">
                    {participant.email}
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-1">
                <Mic className="h-4 w-4 text-gray-400" />
                <Video className="h-4 w-4 text-gray-400" />

                {isHost && participant.role !== "host" && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuLabel>Participant Actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleMuteParticipant(participant.id)}
                      >
                        <MicOff className="h-4 w-4 mr-2" />
                        <span>Mute participant</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleMuteParticipant(participant.id)}
                      >
                        <VideoOff className="h-4 w-4 mr-2" />
                        <span>Disable video</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleRemoveParticipant(participant.id)}
                        className="text-red-600"
                      >
                        <span>Remove from event</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default Participants;
